import { GoabCallout, GoabCircularProgress, GoabContainer, GoabIcon, GoabTable } from "@abgov/react-components"
import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import ApiService from "../../services/ApiService";
import { UserProfileResponse, UserProfileUser } from "../../common/models/userProfile";
import { Link } from "react-router-dom";

const pageStyles = {
  display: "flex",
  flexDirection: "column" as const,
  gap: "1rem",
};

const labelStyles = {
  width: "240px",
  fontWeight: 700,
  padding: "0.5rem 0.25rem",
  verticalAlign: "top" as const,
};

const valueStyles = {
  padding: "0.5rem 0.25rem",
  verticalAlign: "top" as const,
};

const getLabelValue = (value?: string | number | null) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  return String(value);
};

const formatDate = (value?: string | null) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleDateString('en-CA');
};

const userDetailRows: Array<{ label: string; getValue: (user: UserProfileUser) => string }> = [
  { label: "First name", getValue: (user) => getLabelValue(user.firstName) },
  { label: "Last name", getValue: (user) => getLabelValue(user.lastName) },
  { label: "Middle name", getValue: (user) => getLabelValue(user.middleName) },
  { label: "Display name", getValue: (user) => getLabelValue(user.displayName) },
  { label: "Email", getValue: (user) => getLabelValue(user.email) },
  { label: "Phone", getValue: (user) => getLabelValue(user.phone) },
  { label: "Organization", getValue: (user) => getLabelValue(user.organization) },
  { label: "Forest Area", getValue: (user) => getLabelValue(user.usernameAdministeredBy) },
  {
    label: "Access ended",
    getValue: (user) => (getLabelValue(user.accessEnded).toLowerCase() === "yes" ? "Yes" : "No"),
  },
  { label: "Access Start Date", getValue: (user) => formatDate(user.accessStartDate) },
  { label: "Access End Date", getValue: (user) => formatDate(user.accessEndDate) },
  { label: "Vendor IDs", getValue: (user) => getLabelValue(user.vendorId) },
  { label: "Local government", getValue: (user) => getLabelValue(user.localGovernment) },
];

export const MyAccount = () => {
  const auth = useAuth();
  const [profile, setProfile] = useState<UserProfileResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!auth.user?.access_token) {
      return;
    }

    setLoading(true);
    setError(null);

    ApiService.getMyProfile(auth.user.access_token)
      .then((response) => {
        setProfile(response);
      })
      .catch(() => {
        setError("Unable to load your profile.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [auth.user?.access_token]);

  const user = profile?.user;
  const sortedRoles = (profile?.roles ?? []).slice().sort((a, b) =>
    getLabelValue(a.roleName).localeCompare(getLabelValue(b.roleName), undefined, {
      sensitivity: "base",
    })
  );

  return (<>
    <div style={pageStyles}>
      <section className="breadcrumbs">
        <Link to="/">
          <section className="breadcrumbs">
            User maintenance
          </section>
        </Link>
        {(user?.firstName || user?.lastName) &&
          <Link to="/my-account">
            <section className="breadcrumbs">
              <GoabIcon size="small" type="arrow-back"></GoabIcon>
              {`${user?.firstName || ""} ${user?.middleName || ""} ${user?.lastName || ""}`}
            </section>
          </Link>
        }
      </section>
      <h1>User Profile</h1>
      {(auth.isLoading || loading) && <GoabCircularProgress visible={true} />}

      {error && <GoabCallout type="important" heading="Profile unavailable">{error}</GoabCallout>}

      {!auth.isLoading && !loading && user && <>
        <GoabContainer>
          <h2><strong>User Details</strong></h2>
          <table>
            {userDetailRows.map((row) => (
              <tr key={row.label}>
                <td style={labelStyles}>{row.label}</td>
                <td style={valueStyles}>{row.getValue(user)}</td>
              </tr>
            ))}
          </table>
        </GoabContainer>

        <GoabContainer>
          <h2>Roles Assigned</h2>
          <GoabTable>
            <thead>
              <tr>
                <th>Roles</th>
                <th>Role Effective Date</th>
                <th>Role Termination Date</th>
              </tr>
            </thead>
            {sortedRoles.length === 0 && (
              <tr>
                <td colSpan={3}>No roles assigned.</td>
              </tr>
            )}
            {sortedRoles.map((role) => (
              <tr key={`${role.roleId}-${role.grantedStart}`}>
                <td>{getLabelValue(role.roleName)}</td>
                <td>{formatDate(role.grantedStart)}</td>
                <td>{formatDate(role.grantedEnd)}</td>
              </tr>
            ))}
          </GoabTable>
        </GoabContainer>
      </>
      }
    </div>
  </>)
}


